/**
 * WLED Outputs Store
 * Manages WLED device GPIO outputs and their configurations
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, subscribeToTable, type Subscription, type ChangePayload, TABLES } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { useWledDevicesStore } from './wledDevices'
import { useLightsStore } from './lights'
import { wledLogger, wledDeviceService, type WledHardwareOutput, LED_TYPE_NAMES, COLOR_ORDER_NAMES } from '@/services/wled/WledDeviceService'

export interface WledOutput {
  id: string
  wled_device_id: string
  output_index: number
  gpio_pin: number
  start_led: number
  led_count: number
  led_type: string
  is_rgbw: boolean
  color_order: string
  name: string | null
  created_at: string
  updated_at: string
}

export interface CreateWledOutputData {
  wled_device_id: string
  output_index: number
  gpio_pin: number
  start_led: number
  led_count: number
  led_type: string
  is_rgbw: boolean
  color_order: string
  name?: string
}

export const useWledOutputsStore = defineStore('wledOutputs', () => {
  // State
  const outputs = ref<Map<string, WledOutput>>(new Map())
  
  // Realtime subscription
  let realtimeSubscription: Subscription | null = null
  
  // Loading states
  const isLoading = ref(false)
  const isSyncing = ref(false)
  
  // Error state
  const error = ref<string | null>(null)

  // Getters - access .size to track Map changes for proper reactivity
  const allOutputs = computed(() => {
    // Reading .size ensures Vue tracks Map mutations  
    const size = outputs.value.size
    return Array.from(outputs.value.values())
  })
  
  const getOutputsByDevice = (deviceId: string) => {
    return allOutputs.value
      .filter(o => o.wled_device_id === deviceId)
      .sort((a, b) => a.output_index - b.output_index)
  }

  const getOutput = (outputId: string) => {
    return outputs.value.get(outputId)
  }

  // Actions
  const setError = (message: string | null) => {
    error.value = message
  }

  const clearError = () => {
    error.value = null
  }

  /**
   * Load WLED outputs from database
   */
  const fetchOutputs = async (): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return
    }

    try {
      isLoading.value = true
      clearError()

      const provider = getDataProvider()
      
      // Query all wled_outputs - will be filtered by RLS
      const data = await provider.query<WledOutput>('wled_outputs')

      // Clear and repopulate
      outputs.value.clear()
      for (const output of data || []) {
        outputs.value.set(output.id, output)
      }

      wledLogger.info(`Loaded ${outputs.value.size} WLED outputs`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load WLED outputs'
      setError(message)
      wledLogger.error('Error loading WLED outputs:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sync outputs from WLED device hardware config
   */
  const syncOutputsFromDevice = async (deviceId: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    const wledDevicesStore = useWledDevicesStore()
    const device = wledDevicesStore.allDevices.find(d => d.id === deviceId)
    if (!device) {
      return { success: false, error: 'Device not found' }
    }

    try {
      isSyncing.value = true
      clearError()

      // Get state from device to read segments (not hardware config)
      const state = await wledDeviceService.getState(device.ip_address)
      if (!state) {
        return { success: false, error: 'Failed to get state from device' }
      }

      // Also get hardware config to know RGBW capability per output
      const config = await wledDeviceService.getHardwareConfig(device.ip_address)
      const hardwareOutputs = config?.hw?.led?.ins || []

      const segments = state.seg || []
      wledLogger.info(`Found ${segments.length} segments on device ${device.name}`)
      wledLogger.info(`Raw segments data:`, JSON.stringify(segments))

      const provider = getDataProvider()
      const existingOutputs = getOutputsByDevice(deviceId)

      // Sync each segment as an "output" (we're treating segments as the unit of control)
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]
        // Use segment's actual ID if available, otherwise use array index
        const segmentId = seg.id ?? i
        const existing = existingOutputs.find(o => o.output_index === segmentId)
        
        // Determine RGBW capability - check if any hardware output is RGBW
        // In a multi-output setup, a segment might span multiple outputs
        const isRgbw = hardwareOutputs.some(hw => hw.rgbw === true)
        
        // Get segment LED range - WLED uses 'start' and 'stop' (stop is exclusive)
        const startLed = seg.start ?? 0
        const stopLed = seg.stop ?? 0
        const segLen = stopLed - startLed // len = stop - start per WLED docs
        
        // Get segment name from WLED (the 'n' property)
        const segmentName = seg.n || null
        
        wledLogger.info(`Segment ${segmentId}: start=${startLed}, stop=${stopLed}, len=${segLen}, name="${segmentName}"`)

        const outputData: CreateWledOutputData = {
          wled_device_id: deviceId,
          output_index: segmentId, // Use segment's actual ID
          gpio_pin: 0, // Segments don't have GPIO pins
          start_led: startLed,
          led_count: segLen,
          led_type: 'Segment',
          is_rgbw: isRgbw,
          color_order: 'GRB',
          // Use segment name from WLED, or preserve existing name
          name: segmentName || existing?.name || undefined
        }

        if (existing) {
          // Update existing output - include name if WLED provides one
          const updateData: Partial<WledOutput> = {
            start_led: outputData.start_led,
            led_count: outputData.led_count,
            is_rgbw: outputData.is_rgbw
          }
          // Only update name if WLED provides a name (don't overwrite with null)
          if (segmentName) {
            updateData.name = segmentName
          }
          
          const updateResult = await provider.update<WledOutput>('wled_outputs', existing.id, updateData)

          if (updateResult.success && updateResult.data) {
            outputs.value.set(updateResult.data.id, updateResult.data)
          }
        } else {
          // Create new output
          const insertResult = await provider.insert<WledOutput>('wled_outputs', outputData)

          if (insertResult.success && insertResult.data) {
            outputs.value.set(insertResult.data.id, insertResult.data)
          }
        }
      }

      // Remove outputs that no longer exist as segments (and their lights)
      // Build a set of segment IDs that still exist
      const activeSegmentIds = new Set(segments.map((seg, i) => seg.id ?? i))
      
      const lightsStore = useLightsStore()
      for (const existing of existingOutputs) {
        if (!activeSegmentIds.has(existing.output_index)) {
          // Remove associated light first
          const associatedLight = lightsStore.getLightByWledSegment(deviceId, existing.output_index)
          if (associatedLight) {
            await lightsStore.removeLight(associatedLight.id)
            wledLogger.info(`Removed light for deleted segment ${existing.output_index}`)
          }
          
          await provider.delete('wled_outputs', existing.id)
          outputs.value.delete(existing.id)
        }
      }

      // Auto-create lights for each segment if they don't exist
      const updatedOutputs = getOutputsByDevice(deviceId)
      for (const output of updatedOutputs) {
        const existingLight = lightsStore.getLightByWledSegment(deviceId, output.output_index)
        if (!existingLight) {
          await lightsStore.registerWledSegmentLight({
            wledDeviceId: deviceId,
            wledOutputId: output.id,
            segmentId: output.output_index,
            name: output.name || `${device.name} - Segment ${output.output_index + 1}`,
            startLed: output.start_led,
            ledCount: output.led_count,
            isRgbw: output.is_rgbw
          })
          wledLogger.info(`Created light for segment ${output.output_index} on ${device.name}`)
        }
      }

      wledLogger.success(`Synced ${hardwareOutputs.length} outputs for device ${device.name}`)
      return { success: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync outputs'
      setError(message)
      wledLogger.error('Error syncing WLED outputs:', err)
      return { success: false, error: message }
    } finally {
      isSyncing.value = false
    }
  }

  /**
   * Update output name
   */
  const updateOutputName = async (outputId: string, name: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const provider = getDataProvider()
      const output = outputs.value.get(outputId)
      
      const result = await provider.update<WledOutput>('wled_outputs', outputId, { name })

      if (!result.success) {
        throw new Error(result.error || 'Failed to update output name')
      }

      if (result.data) {
        outputs.value.set(result.data.id, result.data)
        
        // Also update the associated light name if it exists
        if (output) {
          const lightsStore = useLightsStore()
          const light = lightsStore.getLightByWledSegment(output.wled_device_id, output.output_index)
          if (light) {
            await lightsStore.updateLightInfo(light.id, { name })
            wledLogger.info(`Also updated light name for output ${output.output_index}`)
          }
        }
      }

      wledLogger.success(`Updated output name to "${name}"`)
      return { success: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update output name'
      setError(message)
      wledLogger.error('Error updating output name:', err)
      return { success: false, error: message }
    }
  }

  /**
   * Subscribe to realtime changes
   */
  const subscribeToChanges = async (): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.user) return

    // Unsubscribe from existing subscription
    if (realtimeSubscription) {
      await realtimeSubscription.unsubscribe()
    }

    try {
      realtimeSubscription = await subscribeToTable(
        'wled_outputs',
        (payload: ChangePayload<WledOutput>) => {
          wledLogger.debug('WLED outputs realtime event:', payload.eventType)

          switch (payload.eventType) {
            case 'INSERT':
              if (payload.new) {
                outputs.value.set(payload.new.id, payload.new)
              }
              break
            case 'UPDATE':
              if (payload.new) {
                outputs.value.set(payload.new.id, payload.new)
              }
              break
            case 'DELETE':
              if (payload.old?.id) {
                outputs.value.delete(payload.old.id)
              }
              break
          }
        }
      )

      wledLogger.info('Subscribed to WLED outputs changes')
    } catch (err) {
      wledLogger.error('Failed to subscribe to WLED outputs changes:', err)
    }
  }

  /**
   * Remove all outputs for a device (called when device is deleted)
   * Database cascade handles actual deletion, this clears local state
   */
  const removeOutputsForDevice = (deviceId: string): void => {
    const deviceOutputs = getOutputsByDevice(deviceId)
    for (const output of deviceOutputs) {
      outputs.value.delete(output.id)
    }
    wledLogger.info(`Removed ${deviceOutputs.length} outputs from local state for device ${deviceId}`)
  }

  /**
   * Cleanup subscriptions
   */
  const cleanup = async (): Promise<void> => {
    if (realtimeSubscription) {
      await realtimeSubscription.unsubscribe()
      realtimeSubscription = null
    }
    outputs.value.clear()
  }

  return {
    // State
    outputs,
    isLoading,
    isSyncing,
    error,

    // Getters
    allOutputs,
    getOutputsByDevice,
    getOutput,

    // Actions
    fetchOutputs,
    syncOutputsFromDevice,
    updateOutputName,
    removeOutputsForDevice,
    subscribeToChanges,
    cleanup,
    setError,
    clearError
  }
})
